import { useMemo } from 'react';

import { EOfferState } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/dto/ISpecOffer';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import {
    BlockContainer,
    FullWidthListContainer,
    IndentContainer,
    ListContainer,
    NonStaticContainer,
} from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { Drawer } from '~/src/components/Drawer';
import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import { useConnectOffer } from '~/src/features/specialOffersContainer/hooks';
import { IProps } from '~/src/features/specialOffersContainer/types';

import AdvantagesList from './components/Advantages';
import Benefits from './components/Benefits';
import Сharacteristics from './components/Characteristics';
import HeaderImage from './components/HeaderImage';
import PromotionTerms from './components/PromotionTerms';

import * as ST from './styled';

const SpecOfferDetails = ({ isPopupOpen, offer, callbackMap, onClose }: IProps<typeof DefaultCallback>): JSX.Element | null => {
    const { isLocalLoading, activateButtonText, connectOffer } = useConnectOffer(callbackMap, offer);

    const isShowFooter = useMemo(
        () => offer?.navigation?.type !== 'promoPacket' || offer?.state !== EOfferState.CONNECTED,
        [offer?.navigation?.type, offer?.state],
    );

    if (!offer) {
        return null;
    }

    const {
        backgroundColor,
        colorLabels,
        imageV2,
        advertising,
        title,
        textColor,
        benefitsV2,
        details,
        properties,
        terms,
        buttonText,
        isLoading,
        isConnecting,
    } = offer;

    const showTermsHandler = () => {
        specialOfferAnalytics.showOfferDetails(offer);
    };

    return (
        <Drawer isOpen={isPopupOpen} onClose={onClose}>
            <ST.SContent bgColor={backgroundColor}>
                <FullWidthListContainer $direction={'column'} $desktop={40} $mobile={32}>
                    <FullWidthListContainer $direction={'column'} $desktop={32} $mobile={24}>
                        {imageV2 && (
                            <IndentContainer $united={[8, 8, 0]}>
                                <HeaderImage
                                    image={{ ...imageV2, alt: title }}
                                    labels={colorLabels}
                                    advertising={advertising}
                                />
                            </IndentContainer>
                        )}

                        <IndentContainer $desktop={[0, 32]} $mobile={[0, 24]}>
                            <ST.Title $textColor={textColor} $withIndent={false} $mobile dangerouslySetInnerHTML={{ __html: title }}/>
                        </IndentContainer>

                        <Benefits benefits={benefitsV2}/>
                    </FullWidthListContainer>

                    <BlockContainer
                        rounder={{ $desktop: 32, $mobile: 24 }}
                        indent={{ $desktop: [32], $mobile: [24] }}
                        bgColor={COLORS.BgMain}
                    >
                        <FullWidthListContainer $direction={'column'} $desktop={40} $mobile={24}>
                            <AdvantagesList advantages={details}/>

                            <ListContainer $direction={'column'} $desktop={24} $mobile={16}>
                                <Сharacteristics characteristics={properties}/>

                                <PromotionTerms terms={terms} onShowTerms={showTermsHandler}/>
                            </ListContainer>
                        </FullWidthListContainer>
                    </BlockContainer>

                    {isShowFooter && (
                        <NonStaticContainer
                            $united={{
                                $position: 'sticky',
                                $left: 0,
                                $bottom: 0,
                                $right: 0,
                            }}
                        >
                            <ST.FooterContent $desktop={[24, 32]} $mobile={[16]}>
                                <ButtonGroup>
                                    <Button
                                        bType={'action'}
                                        size={'medium'}
                                        loading={isConnecting || isLocalLoading}
                                        disabled={isLoading}
                                        onClick={connectOffer}
                                    >
                                        {buttonText || activateButtonText}
                                    </Button>
                                </ButtonGroup>
                            </ST.FooterContent>
                        </NonStaticContainer>
                    )}
                </FullWidthListContainer>
            </ST.SContent>
        </Drawer>
    );
};

export default SpecOfferDetails;
